import axios from "axios";
import { auth } from "../config/firebase";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5000",
});

// ─── Upgrade handler hook ─────────────────────────────────────────────────────
// PaymentContext registers a callback so the interceptor can trigger the
// upgrade modal without importing React code.
let _upgradeHandler = null;
export function setUpgradeHandler(fn) {
  _upgradeHandler = fn;
}

// ─── Request Interceptor ──────────────────────────────────────────────────────
// Attaches a fresh Firebase ID token to every outgoing request.
// Firebase SDK auto-refreshes tokens silently; getIdToken() returns a
// cached token or waits for an in-progress refresh automatically.
API.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Response Interceptor ─────────────────────────────────────────────────────
// On 401, force a token refresh once and retry the original request.
// On 429 with an upgrade payload, trigger the upgrade modal.
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // ── 429 — rate-limited, show upgrade modal ──
    if (error.response?.status === 429 && error.response.data?.upgrade) {
      if (_upgradeHandler) {
        _upgradeHandler(error.response.data.upgrade);
      }
      return Promise.reject(error);
    }

    // ── 401 — token expired, try one silent refresh ──
    if (
      error.response?.status === 401 &&
      !originalRequest._retried &&
      auth.currentUser
    ) {
      originalRequest._retried = true;
      try {
        const freshToken = await auth.currentUser.getIdToken(true);
        originalRequest.headers.Authorization = `Bearer ${freshToken}`;
        return API(originalRequest);
      } catch {
        // Force refresh failed — token is unrecoverable; propagate the 401
      }
    }

    return Promise.reject(error);
  }
);

export const generateMessage = (data) => API.post("/api/generate", data);
export const generateFollowUp = (data) => API.post("/api/followup", data);
export const getHistory = () => API.get("/api/generate/history");
export const updateHistoryStatus = (id, applicationStatus) =>
  API.patch(`/api/generate/history/${id}/status`, { applicationStatus });

// ─── Reminders ────────────────────────────────────────────────────────────────
export const createReminder = (data) => API.post("/api/reminders", data);
export const getReminders = () => API.get("/api/reminders");
export const cancelReminder = (id) => API.delete(`/api/reminders/${id}`);

export default API;